"use client";

import { RelationshipDialog } from "@/components/relationship-dialog";
import { useAuth } from "@/hooks/use-auth";
import actionMapping from "@/info/actions-relationship-mapping.json";
import { cloneElement, ReactElement, useCallback, useEffect, useState } from "react";
type RelationshipType = "buyer" | "seller" | "both";

interface ActionGuardProps {
	actionId: string;
	children: ReactElement;
	fallbackComponent?: ReactElement;
	customFeatureName?: string;
	customDescription?: string;
	onBeforeGuard?: () => void;
}

interface ActionConfig {
	id: string;
	label: string;
	location: string | string[];
	handler: string;
	description: string;
	priority: string;
	status?: string;
}

/**
 * ActionGuard component that wraps action buttons/elements and validates user relationship
 * against the action requirements defined in the JSON mapping file.
 *
 * @param actionId - The ID of the action from the JSON mapping (e.g., "list_property", "request_visit")
 * @param children - The action element/button to be guarded
 * @param fallbackComponent - Optional component to render when user doesn't have access
 * @param customFeatureName - Override the feature name shown in the modal
 * @param customDescription - Override the description shown in the modal
 */
export function ActionGuard({
	actionId,
	children,
	fallbackComponent,
	customFeatureName,
	customDescription,
	onBeforeGuard,
}: ActionGuardProps) {
	// ===== ALL HOOKS MUST BE AT THE TOP =====
	const { user, isAuthenticated } = useAuth();
	const [showGuard, setShowGuard] = useState(false);

	// Memoized handler to prevent infinite re-renders
	const handleClick = useCallback(
		(e?: React.MouseEvent) => {
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			if (onBeforeGuard) {
				onBeforeGuard();
				// Delay showing guard to allow sidebar to close
				setTimeout(() => {
					setShowGuard(true);
				}, 300);
			} else {
				setShowGuard(true);
			}
		},
		[onBeforeGuard],
	);

	// Find the action in the mapping
	const getActionInfo = (): { relationship: RelationshipType; action: ActionConfig } | null => {
		for (const [relationshipKey, relationshipData] of Object.entries(actionMapping.actions)) {
			const relationship = relationshipKey as RelationshipType;
			const action = relationshipData.buttons.find((button: ActionConfig) => button.id === actionId);

			if (action) {
				return { relationship, action };
			}
		}
		return null;
	};

	const actionInfo = getActionInfo();
	const currentUserRelationship = user?.relation as "buyer" | "seller" | undefined;

	useEffect(() => {
		// Only auto-close if we have action info and the user has the required relationship
		if (showGuard && actionInfo && currentUserRelationship === actionInfo.relationship) {
			setShowGuard(false);
		}
	}, [showGuard, actionInfo, currentUserRelationship]);

	// ===== CONDITIONAL LOGIC AFTER ALL HOOKS =====

	// If action not found in mapping, render children as-is (fail open)
	if (!actionInfo) {
		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.warn(`ActionGuard: Action "${actionId}" not found in mapping. Rendering without guard.`);
		}
		return children;
	}

	// Now we can safely destructure since we've checked actionInfo exists
	const { relationship: requiredRelationship, action } = actionInfo;
	const hasRequiredRelationship = currentUserRelationship === requiredRelationship;

	// If not authenticated, render children as-is (authentication handled separately)
	if (!isAuthenticated) {
		return children;
	}

	// If action is available to both relationships, no guard needed
	if (requiredRelationship === "both") {
		return children;
	}

	// If user has required relationship, render children
	if (hasRequiredRelationship) {
		return children;
	}

	// Clone the children and intercept the onClick handler
	const originalProps = children.props as Record<string, unknown>;

	// Check if this is a Link component (has href prop)
	const isLinkComponent = "href" in originalProps;

	// Remove original handlers and add our interceptor
	const { onClick: _originalOnClick, onPress: _originalOnPress, href: _originalHref, ...restProps } = originalProps;

	const guardedChildren = cloneElement(children, {
		...restProps,
		// For Link components, replace href with # to prevent navigation but keep Link happy
		...(isLinkComponent ? { href: "#" } : {}),
		// Add our click handler that prevents default navigation
		onClick: handleClick,
		// Make it look clickable
		style: { cursor: "pointer", ...((originalProps.style as object) || {}) },
	} as Record<string, unknown>);

	// Log before rendering the guard modal
	const shouldRenderGuard = showGuard && currentUserRelationship;

	return (
		<>
			{fallbackComponent && !hasRequiredRelationship ? fallbackComponent : guardedChildren}

			{/* Relationship Guard Modal */}
			{shouldRenderGuard && (
				<RelationshipDialog
					isOpen={showGuard}
					onClose={() => {
						setShowGuard(false);
					}}
					currentRelationship={currentUserRelationship}
					requiredRelationship={requiredRelationship}
					featureName={customFeatureName || action.label}
					description={customDescription || action.description}
				/>
			)}
		</>
	);
}

/**
 * Hook to check if current user has access to a specific action
 * @param actionId - The ID of the action from the JSON mapping
 * @returns Object with access info
 */
export function useActionAccess(actionId: string) {
	const { user, isAuthenticated } = useAuth();

	const getActionInfo = (): { relationship: RelationshipType; action: ActionConfig } | null => {
		for (const [relationshipKey, relationshipData] of Object.entries(actionMapping.actions)) {
			const relationship = relationshipKey as RelationshipType;
			const action = relationshipData.buttons.find((button: ActionConfig) => button.id === actionId);

			if (action) {
				return { relationship, action };
			}
		}
		return null;
	};

	const actionInfo = getActionInfo();

	if (!actionInfo) {
		return {
			hasAccess: true, // Fail open if action not found
			requiredRelationship: null,
			currentRelationship: user?.relation || null,
			actionExists: false,
		};
	}

	const { relationship: requiredRelationship } = actionInfo;
	const currentUserRelationship = user?.relation;

	const hasAccess =
		!isAuthenticated || // Not authenticated users have access (auth handled separately)
		requiredRelationship === "both" || // Actions available to both
		currentUserRelationship === requiredRelationship; // User has required relationship

	return {
		hasAccess,
		requiredRelationship,
		currentRelationship: currentUserRelationship || null,
		actionExists: true,
		action: actionInfo.action,
	};
}

/**
 * Utility function to get all actions available to a specific relationship
 * @param relationship - The relationship type
 * @returns Array of available actions
 */
export function getActionsForRelationship(relationship: RelationshipType): ActionConfig[] {
	const actions: ActionConfig[] = [];

	// Add actions specific to the relationship
	if (actionMapping.actions[relationship]) {
		actions.push(...actionMapping.actions[relationship].buttons);
	}

	// Add actions available to both
	if (relationship !== "both" && actionMapping.actions.both) {
		actions.push(...actionMapping.actions.both.buttons);
	}

	return actions;
}
